import {BaseElement, BASIS, OSkinableInit} from "back/commons/basis";
import {Button, ButtonActions} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {IReg, REG} from "lib/commons/registry";
import {IView, IViewsContainer, OViewVisitOptions} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {Grid, GridSmall, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridColDef, IGridLineDrawer} from "back/commons/widgets/grid-core";
import {CellBuilderIconLabel, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {RegisteredSkin, STYLER} from "lib/styler/styler";
import {IWspUiEnv, Wsp} from "lib/wsp/wsp";
import {GridDataHolderJsonTree} from "back/commons/widgets/tree";
import {SKINPACK} from "lib/wsp/pack";
import JSkinPackSkin = STYLER.JSkinPackSkin;

/**
 * Dialogue de sélection d'un skin présent dans le contexte, associé à un skinCLassStyler donné
 * @return : skin sélectionné, ou null
 */
export interface SkinSelectorForSkinClassStl extends BaseElement, IView {
	initialize(init: OSkinSelectorForSkinClassStlInit): this
}

export interface OSkinSelectorForSkinClassStlInit extends OSkinableInit {
	skinClassStl: string
	skinGrid?: OGridSmallInit
	buttonLabel?: string
	onSelChange?: (sel: OSkinSelectorEntry) => void
	startSkinSel?: string;
	/** Retourne true si le skin est conservé */
	skinMatchFilter?: (skin: RegisteredSkin) => boolean
}

export interface OSkinSelectorEntry {
	code: string
	name: string
	iconUrl: string
	resUrl: string
}

export interface OSkinSelectorOwner extends OSkinSelectorEntry {
	version: string
	ch: OSkinSelectorEntry[]
	isOwner: true
}

export class SkinSelectorForSkinClassStl extends BaseElement implements IViewsContainer, IGridLineDrawer {
	reg: IReg<IWspUiEnv>;
	wsp: Wsp;

	protected conf: OSkinSelectorForSkinClassStlInit;

	protected skinGrid: GridSmall;
	protected skinDH = new GridDataHolderJsonTree<OSkinSelectorOwner | OSkinSelectorEntry>('ch');

	msgArea: HTMLElement;
	selectBtn: ButtonActions<SkinSelectorForSkinClassStl>;

	protected _initialize(init: OSkinSelectorForSkinClassStlInit) {
		this.reg = this.findReg(init);
		this.wsp = this.reg.env.wsp;
		this.conf = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		REG.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);

		this.skinGrid = sr.appendChild(new GridSmall().initialize(Object.assign({
			selType: 'mono',
			autoSelOnFocus: "first",
			columnDefs: [
				//new GridColDef('icon').setMinWidth('60px').setCellBuilder(new CellBuilderSkinImage()),
				new GridColDef('value')
					.setFlex('1rem', 1, 1)
					.setCellBuilder(new CellBuilderIconLabel('name').setIconKey('iconUrl').setDescriptionFunc((row)=>row.getData("name")))
			],
			dataHolder: this.skinDH.setOpenAll(true),
			hideHeaders: true,
			lineDrawer: this,
			skinOver: 'styler-skins-selector/grid',
			emptyBody: (): Node => <c-msg label="Aucun skin compatible installé" level="info"/>
		}, BASIS.newInit(init.skinGrid, this.reg))));

		this.skinGrid.addEventListener("grid-select", this._onGridSelect);

		this.refreshDatasTree();

		const footer = sr.appendChild(<div id="footer"/>);
		this.msgArea = footer.appendChild(<div id="msg"/>);
		this.selectBtn = footer.appendChild(<Button id="select" label={init.buttonLabel || "Sélectionner"} class="default" ui-context="dialog" onclick={this.onSelectBtn}/>) as ButtonActions<SkinSelectorForSkinClassStl>;
		footer.appendChild(<Button id="cancel" label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>);

		if (init.startSkinSel) this.selectBySkin(init.startSkinSel);

		this._refresh();
	}

	_refresh() {
		const selection = this.getSelectedSkin();
		this.selectBtn.disabled = !selection;
	}

	/**
	 * Calcul des datas du tree :
	 * 	- skins issus des wspDef/extDef activés
	 * 	- skins issus des skinPack
	 * @private
	 */
	private async refreshDatasTree(): Promise<void> {
		const datas: Map<String, OSkinSelectorOwner> = new Map();
		const currentSkinClassStl = STYLER.getSkinClassStl(this.wsp, this.conf.skinClassStl);
		const currentSkinClass = currentSkinClassStl?.skinClass(this.wsp);

		// - Skins issus des skinPack
		if (currentSkinClassStl) {
			const skinsFromSkinsPacks: Set<JSkinPackSkin> = new Set();
			const skinsFromSkinsPacksEntries: OSkinSelectorEntry[] = [];
			const list = await STYLER.listSkinsFromSkinsPacks(this.reg.env.universe.packServer, currentSkinClassStl.class, this.reg.env.uiRoot.lang);
			list.forEach((elt) => {
				if (!skinsFromSkinsPacks.has(elt)) {
					skinsFromSkinsPacks.add(elt);
					skinsFromSkinsPacksEntries.push({
						code: elt.code,
						name: elt.title,
						iconUrl: SKINPACK.getIconUrl(this.reg.env.universe.packServer, elt.code),
						resUrl: STYLER.getSkinUrl(this.reg.env.universe.packServer, elt.code, currentSkinClassStl.class, this.reg.env.uiRoot.lang)
					});
				}
			});
			if (skinsFromSkinsPacksEntries.length)
				datas.set('_skinSet', {
					isOwner: true,
					name: 'Habillages graphiques installés',
					ch: skinsFromSkinsPacksEntries
				} as OSkinSelectorOwner)
		}

		// - Skins apportés par des extDef/wspDef
		function skinMatchFilter(this: SkinSelectorForSkinClassStl, skin: RegisteredSkin): boolean {
			if (currentSkinClass && !currentSkinClass.equals(skin.skinClass(this.wsp)))
				return false;
			if (this.conf.skinMatchFilter) return this.conf.skinMatchFilter(skin);
			return true;
		}

		for (const skin of await STYLER.getRegisteredSkins(this.wsp, [currentSkinClassStl.class])) {
			if (skinMatchFilter.call(this, skin)) {
				if (!datas.has(skin.owner.key)) datas.set(skin.owner.key, {
					isOwner: true,
					name: skin.owner.name,
					version: skin.owner.version,
					ch: []
				} as OSkinSelectorOwner);
				const ownerEntry = datas.get(skin.owner.key);
				ownerEntry.ch.push({
					code: skin.code,
					name: skin.name,
					iconUrl: skin.getIconUrl(this.reg.env.wsp.wspServer),
					resUrl: skin.getSrcUrl(this.reg.env.wsp.wspServer)
				});
			}
		}
		this.skinDH.setDatas(Array.from(datas.values()));
	}

	protected _onGridSelect(this: Grid) {
		const me = DOMSH.findHost<SkinSelectorForSkinClassStl>(this);
		me._refresh();
		if (me.conf.onSelChange) me.conf.onSelChange(me.getSelectedSkin());
	}

	/** Skin actuellement sélectionné, ou null */
	getSelectedSkin(): OSkinSelectorEntry | null {
		if (this.skinDH) {
			const row = this.skinDH.getRow(this.skinGrid.getSelectedRow());
			if (!row) return null;
			return (row.rowDatas as OSkinSelectorOwner).isOwner ? null : row.rowDatas as OSkinSelectorEntry;
		}
	}

	/**
	 * Sélection de l'entrée de l'arbre via un code de skin sur un tree **chargé**
	 * Si un skin demandé n'est pas présent dans le tree, aucune erreur n'est remontée
	 */
	selectBySkin(skinCode: string): void {
		if (skinCode != null) {
			const selectedRows: number[] = [];
			for (let i = 0, c = this.skinDH.countRows(); i < c; i++) {
				const skinRow = this.skinDH.getRow(i).rowDatas as OSkinSelectorEntry;
				if (skinRow && skinRow.code === skinCode) {
					selectedRows.push(i);
					break;
				}
			}
			if (selectedRows.length > 0)
				this.skinGrid.setSelectedRows(selectedRows);
			else
				this.skinGrid.clearSel();
		}
	}

	setMsg(msg: string, allowSelect: boolean) {
		this.msgArea.textContent = msg;
		this.selectBtn.disabled = !allowSelect;
	}


	onCancelBtn(this: Button) {
		POPUP.findPopupableParent(this).close();
	}

	onSelectBtn(this: Button) {
		const me = DOMSH.findHost<SkinSelectorForSkinClassStl>(this);
		const r = me.getSelectedSkin();
		if (r) POPUP.findPopupableParent(this).close(r);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		return visitor(this.skinGrid);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return visitor(this.skinGrid);
	}

	redrawLine(row: GridDataRowJson<any>, line: HTMLElement): void {
		const isOwnerRow = (row.rowDatas as OSkinSelectorOwner).isOwner;
		const ownerVersion = (row.rowDatas as OSkinSelectorOwner).version;
		if (isOwnerRow && ownerVersion)
			line.title = ownerVersion
		else
			delete line.title;
		DOM.setAttrBool(line, "data-is-owner", isOwnerRow);
	}
}

REG.reg.registerSkin('styler-skins-selector', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		height: 50vh;
		max-height: 30em;
		min-width: 0;
		flex-direction: column;
		width: 20em;
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
	}
`);

REG.reg.registerSkin('styler-skins-selector/grid', 1, /* language=CSS */ `
	*[data-is-owner] {
		font-weight: bold;
	}
`);

customElements.define("styler-skins-selector", SkinSelectorForSkinClassStl);


/** Cell builder de l'icône du skin */
/*
class CellBuilderSkinImage implements ICellBuilder {
	getColSortFn(): ((r1: IGridDataRow, r2: IGridDataRow) => number) | null {
		return undefined;
	}

	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		const grid = DOMSH.findHost(root) as GridSmall;
		const reg = REG.findReg(grid) as IReg<IWspEnv>;
		DOM.addClass(root, "imgcell")
		let img = root.appendChild(<img src={WSP.skinSetsIconUrl(reg.env.wsp, row.getData("code"))}/>);
		img.onerror = function (this: HTMLImageElement, ev: Event) {
			DOM.setHidden(this, true);
		};
	}
}
*/